import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Flame, Lock, Loader2, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return err?.message || "Something went wrong.";
};

const PortalReset = () => {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setError("This reset link is missing its token. Please request a new one."); return; }
    setError(null); setBusy(true);
    try {
      await api.post("/portal/auth/reset", { token, password });
      setDone(true);
      setTimeout(() => nav("/portal"), 1200);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 ember-bg">
      <div className="w-full max-w-md bento-card !p-9" data-testid="portal-reset">
        <span className="glow-blob" />
        <div className="text-center">
          <div className="icon-badge !h-14 !w-14 mx-auto mb-5">
            <Flame className="h-7 w-7 text-[#F28C18]" strokeWidth={1.5} />
          </div>
          <div className="overline mb-2">Reset your password</div>
          <h1 className="font-serif-display text-3xl text-[#F6F0E6]">Choose a new <span className="italic gold-text">password</span></h1>
        </div>

        {done ? (
          <div className="mt-7 text-center" data-testid="portal-reset-done">
            <CheckCircle2 className="h-12 w-12 text-[#C9A44C] mx-auto mb-3" />
            <p className="text-[#F6F0E6]">Password updated. Redirecting you to the portal…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3">
            <label className="block">
              <span className="sr-only">New password</span>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A44C]/70" strokeWidth={1.6} />
                <input
                  type="password" required minLength={8} maxLength={128} autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 8 characters)"
                  className="w-full bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-xl pl-11 pr-4 py-3 text-[15px] text-[#F6F0E6] placeholder-[#D9D9D9]/45 focus:outline-none focus:border-[#C9A44C]/55"
                  data-testid="reset-password-input"
                />
              </div>
            </label>
            {error && (
              <div className="flex items-start gap-2 text-sm text-[#FCA5A5] bg-[#7f1d1d]/20 border border-[#FCA5A5]/30 rounded-xl px-3 py-2.5" data-testid="reset-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-fire w-full" data-testid="reset-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />}
              {busy ? "Updating…" : "Set new password"}
            </button>
          </form>
        )}

        <Link to="/portal/login" className="mt-7 flex items-center justify-center gap-1 text-xs text-[#D9D9D9]/65 hover:text-[#C9A44C]">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default PortalReset;
